/**
 * Theme management for RSSio
 * Handles light/dark/auto mode toggling and persistence
 */

export type ThemeMode = "light" | "dark" | "auto";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "rssio-theme-mode";

/**
 * Get the current theme mode preference (light/dark/auto)
 */
export function getThemeMode(): ThemeMode {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
        if (stored === "light" || stored === "dark" || stored === "auto") {
            return stored;
        }
    } catch {
        // localStorage might be unavailable
    }
    return "auto"; // Default to auto (system/time-of-day)
}

/**
 * Determine if it should be dark mode based on time of day.
 * Dark between 19:00 and 07:00, light otherwise.
 */
function isDarkByTimeOfDay(): boolean {
    const hour = new Date().getHours();
    return hour >= 19 || hour < 7;
}

/**
 * Get the resolved theme (always light or dark) based on mode and current conditions.
 */
export function getResolvedTheme(): ResolvedTheme {
    const mode = getThemeMode();

    if (mode === "light") return "light";
    if (mode === "dark") return "dark";

    // Auto mode: prefer system preference, fall back to time-of-day
    if (typeof window !== "undefined" && window.matchMedia) {
        try {
            if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
                return "dark";
            }
        } catch {
            // matchMedia may not be available
        }
    }

    // Fall back to time-of-day
    return isDarkByTimeOfDay() ? "dark" : "light";
}

/**
 * Apply a resolved theme to the document by setting data-theme attribute
 */
export function applyTheme(resolved: ResolvedTheme): void {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", resolved);
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute("content", resolved === "dark" ? "#0f172a" : "#ffffff");
    }
    if (typeof window !== "undefined") {
        const w = window as any;
        if (w.Capacitor?.isNativePlatform?.()) {
            w.Capacitor.Plugins?.RssioSync?.setStatusBarTheme?.({ isDark: resolved === "dark" })?.catch?.(() => {});
        }
    }
}

/**
 * Initialize the theme on app startup. Returns the resolved theme.
 */
export function initTheme(): ResolvedTheme {
    const resolved = getResolvedTheme();
    applyTheme(resolved);
    return resolved;
}

/**
 * Set the theme mode and persist the choice.
 * Returns the newly resolved theme.
 */
export function setThemeMode(mode: ThemeMode): ResolvedTheme {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
        // Silently fail if localStorage is unavailable
    }
    const resolved = getResolvedTheme();
    applyTheme(resolved);
    return resolved;
}

/**
 * Cycle through the three theme modes: light → dark → auto → light
 * Returns the new mode and resolved theme
 */
export function cycleTheme(): { mode: ThemeMode; resolved: ResolvedTheme } {
    const current = getThemeMode();
    let next: ThemeMode;
    if (current === "light") next = "dark";
    else if (current === "dark") next = "auto";
    else next = "light";

    const resolved = setThemeMode(next);
    return { mode: next, resolved };
}

/**
 * Listen for system theme changes when in auto mode and re-apply.
 * Returns a cleanup function.
 */
export function watchSystemTheme(onChange: (resolved: ResolvedTheme) => void): () => void {
    if (typeof window === "undefined" || !window.matchMedia) {
        return () => undefined;
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
        if (getThemeMode() === "auto") {
            const resolved = getResolvedTheme();
            applyTheme(resolved);
            onChange(resolved);
        }
    };
    // Modern API
    if (mql.addEventListener) {
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }
    // Legacy API (Safari < 14)
    if ((mql as any).addListener) {
        (mql as any).addListener(handler);
        return () => (mql as any).removeListener(handler);
    }
    return () => undefined;
}